import { useState } from 'react';
import { Modal, Button, ErrorText } from './ui';

export function NameDialog({
  title,
  label,
  initial = '',
  submitLabel = 'Create',
  extraField,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  initial?: string;
  submitLabel?: string;
  /** Optional second field (e.g. description/notes). */
  extraField?: { label: string; placeholder?: string };
  onSubmit: (value: string, extra: string) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    if (!value.trim()) {
      setError('This field is required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSubmit(value.trim(), extra.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <label className="text-sm font-medium text-slate-600">{label}</label>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !extraField) submit();
        }}
        className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 outline-none focus:border-brand-500"
      />
      {extraField && (
        <div className="mt-3">
          <label className="text-sm font-medium text-slate-600">{extraField.label}</label>
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder={extraField.placeholder}
            rows={2}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 outline-none focus:border-brand-500"
          />
        </div>
      )}
      <ErrorText>{error}</ErrorText>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Working…' : submitLabel}
        </Button>
      </div>
    </Modal>
  );
}
